import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { Rule } from "@/components/marketing/Rule";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useConsent } from "@/components/consent/ConsentProvider";
import { toast } from "sonner";

const COLUMNS = [
  {
    key: "product",
    links: [
      { key: "features", to: "/fonctionnalites" },
      { key: "pricing", to: "/tarifs" },
      { key: "comparator", to: "/tarifs/comparatif" },
      { key: "howItWorks", to: "/comment-ca-marche" },
    ],
  },
  {
    key: "useCases",
    links: [
      { key: "soloEditors", to: "/pour/editeurs-solo" },
      { key: "agencies", to: "/pour/agences-medias" },
      { key: "pressGroups", to: "/pour/groupes-presse" },
      { key: "showcase", to: "/vitrine" },
    ],
  },
  {
    key: "resources",
    links: [
      { key: "blog", to: "/ressources" },
      { key: "guides", to: "/ressources/guides" },
      { key: "caseStudies", to: "/vitrine" },
      { key: "faq", to: "/faq" },
      { key: "comparators", to: "/ressources/comparatifs" },
    ],
  },
  {
    key: "company",
    links: [
      { key: "about", to: "/a-propos" },
      { key: "contact", to: "/contact" },
      { key: "trust", to: "/securite" },
    ],
  },
  {
    key: "legal",
    links: [
      { key: "legalNotice", to: "/mentions-legales" },
      { key: "cgv", to: "/cgv" },
      { key: "rgpd", to: "/politique-rgpd" },
      { key: "cookies", to: "/cookies" },
      { key: "subprocessors", to: "/trust" },
    ],
  },
] as const;

function NewsletterForm() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "ok">("idle");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    try {
      const { error } = await supabase.functions.invoke("marketing-newsletter-subscribe", {
        body: { email, locale: i18n.language, source: "footer" },
      });
      if (error) throw error;
      setStatus("ok");
      setEmail("");
      toast.success(t("marketing.footer.newsletter.successToast"));
    } catch {
      setStatus("idle");
      toast.error(t("marketing.footer.newsletter.errorToast"));
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex w-full max-w-md flex-col gap-2">
      <label htmlFor="newsletter-email" className="dateline">
        {t("marketing.footer.newsletter.label")}
      </label>
      <div className="flex gap-2">
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("marketing.footer.newsletter.placeholder")}
          className="h-11 flex-1 border border-rule bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
        />
        <button
          type="submit"
          disabled={status !== "idle"}
          className="inline-flex h-11 items-center bg-accent-press px-4 text-xs font-semibold uppercase tracking-wider text-accent-press-foreground disabled:opacity-60"
        >
          {status === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : status === "ok" ? <Check className="h-4 w-4" /> : t("marketing.footer.newsletter.submit")}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">{t("marketing.footer.newsletter.consent")}</p>
    </form>
  );
}

function MadeInFrancePictogram({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("inline-flex items-center gap-1", className)}>
      <span className="flex h-3 w-5 items-stretch overflow-hidden border border-rule">
        <span className="flex-1 bg-[#003399]" />
        <span className="flex-1 bg-background" />
        <span className="flex-1 bg-[#FFCC00]" />
      </span>
      <span className="flex h-3 w-5 items-stretch overflow-hidden border border-rule">
        <span className="flex-1 bg-[#0055A4]" />
        <span className="flex-1 bg-background" />
        <span className="flex-1 bg-[#EF4135]" />
      </span>
    </span>
  );
}

export function MarketingFooter() {
  const { t } = useTranslation();
  const { open: openCookiePrefs } = useConsent();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-rule bg-paper text-ink dark:bg-paper dark:text-ink">
      <div className="mx-auto max-w-[1200px] px-5 py-12 md:py-16">
        <div className="dateline flex flex-wrap items-center gap-x-6 gap-y-2">
          <span>delapresse.fr</span>
          <span aria-hidden className="text-rule">·</span>
          <span>{t("marketing.footer.band.editedBy")}</span>
          <span aria-hidden className="text-rule">·</span>
          <span>{t("marketing.footer.band.euHosting")}</span>
          <span aria-hidden className="text-rule">·</span>
          <span>{t("marketing.footer.band.selectableAi")}</span>
        </div>

        <Rule className="my-10" />

        <div className="grid gap-10 md:hidden">
          <div>
            <Link to="/" className="text-display text-2xl tracking-tight">delapresse</Link>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
              {t("marketing.footer.tagline")}
            </p>
          </div>
          <NewsletterForm />
          <Accordion type="multiple" className="border-y border-rule">
            {COLUMNS.map((col) => (
              <AccordionItem key={col.key} value={col.key} className="border-b border-rule last:border-b-0">
                <AccordionTrigger className="py-3 text-sm font-semibold uppercase tracking-wider">
                  {t(`marketing.footer.columns.${col.key}.title`)}
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2 pb-3">
                    {col.links.map((l) => (
                      <li key={l.key}>
                        <Link to={l.to} className="text-sm text-muted-foreground hover:text-foreground">
                          {t(`marketing.footer.columns.${col.key}.links.${l.key}`)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="hidden gap-10 md:grid md:grid-cols-[1.4fr_repeat(5,1fr)]">
          <div>
            <Link to="/" className="text-display text-2xl tracking-tight">delapresse</Link>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {t("marketing.footer.tagline")}
            </p>
            <div className="mt-6">
              <NewsletterForm />
            </div>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.key}>
              <h3 className="dateline mb-3">{t(`marketing.footer.columns.${col.key}.title`)}</h3>
              <ul className="space-y-2 text-sm">
                {col.links.map((l) => (
                  <li key={l.key}>
                    <Link to={l.to} className="text-muted-foreground transition-colors hover:text-foreground">
                      {t(`marketing.footer.columns.${col.key}.links.${l.key}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Rule className="my-10" />

        <div className="flex flex-col gap-4 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span>© {year} delapresse</span>
            <span aria-hidden className="text-rule">·</span>
            <span className="inline-flex items-center gap-2">
              <MadeInFrancePictogram />
              {t("marketing.footer.bottom.madeInFrance")}
            </span>
            <span aria-hidden className="text-rule">·</span>
            <button
              type="button"
              onClick={openCookiePrefs}
              className="hover:text-foreground"
            >
              {t("marketing.footer.bottom.cookieChoices")}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact className="h-8 px-2 text-xs" />
            <ThemeToggle size="sm" />
          </div>
        </div>
      </div>
    </footer>
  );
}
